import { useState, useEffect } from 'react';
import { useApi } from '../../../api/ApiProvider';
import Api from '../../../axiosApi/api';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { CreateNonWorkingDayCommand } from '../../../axiosApi/models/create-non-working-day-command';
import { UpdateNonWorkingDayCommand } from '../../../axiosApi/models/update-non-working-day-command';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RequiredField from '../../common/RequiredField';
import intl from 'components/i18n/ReactIntlWrapper';
import { Grid, Card, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Spinner from '../../Spinner';
import CalendarEventForm from './CalendarEventForm';
import { handleAPIError } from 'common/errorHandler';
import { useDialog } from 'common/DialogProvider';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const CalendarEvent = ({ closeModal, eventId, updateEvents }) => {

    const defaultCalendarEventInput = {
        idCountry: null,
        country: {name: ''},
        description: '',
        date: null
    };

    const [input, setInput] = useState(defaultCalendarEventInput);

    const api: Api = useApi();

    const [errors, setErrors] = useState({});

    const [countries, setCountries] = useState(null);

    const [loading, setLoading] = useState<boolean>(false);

    const [loadingSave, setLoadingSave] = useState<boolean>(false);

    const toast = useToastMessageQueue();

    const fetchCalendarEvent = async (eventId: number) => {
        setLoading(true);
        const response = await api.timeoffApi.apiVversionNonWorkingDayIdGet(eventId, "1", {}).then((response) => {
            if (response.data.data) {
                setInput({
                    idCountry: response?.data?.data.idCountry,
                    country: {name: response?.data?.data.country.labelKey},
                    description: response?.data?.data.description,
                    date: new Date(response?.data?.data.date) 
                });
            };
        }).catch((error) => {
            handleAPIError(error, toast, errors);
            setErrors({ ...errors });
        }).finally(() => {
            setLoading(false);
        });
    };

    const fetchCountries = async () => {
        setLoading(true);
        const response = await api.addressApi.apiVversionAddressCountryAllGet("1", {}).then((response) => {
            if (response.data) {
                setCountries(response.data.data);
            };
        }).catch((error) => {
            handleAPIError(error, toast, errors);
            setErrors({ ...errors });
        }).finally(() => {
            setLoading(false);
        });
    };

    useEffect(() => {
        if (eventId) {
            fetchCalendarEvent(eventId);
        };
        fetchCountries();
    }, [eventId]);

    const handleInputChange = (e) => {
        setInput({
            ...input,
            [e.target.name]: e.target.value
        });
        setErrors({});
    };

    const handleCountryChange = (e) => {
        const inputVal = {
            ...input,
            idCountry: e.id,
            country: {name: e.labelKey}
        };
        setInput({ ...inputVal });
        setErrors({});
    };

    const handleDate = (e) => {
        setInput({
            ...input,
            date: e
        });
        setErrors({});
    };

    const validate = (input) => {
        let errors: any = {};
        if (!input.idCountry) {
            errors.idCountry = intl.get('validate.errors.isRequired');
        }
        if (!input.description) {
            errors.description = intl.get('validate.errors.isRequired');
        }
        if (!input.date) {
            errors.date = intl.get('validate.errors.isRequired');
        }
        setErrors({ ...errors });
        return Object.keys(errors).length > 0 ? errors : {};
    };

    const handleCancel = () => {
        if (eventId) {
            fetchCalendarEvent(eventId);
        };
        setInput(defaultCalendarEventInput);
        setErrors({});
        closeModal();
    };

    const handleSave = async (e) => {
        e.preventDefault();
        const errors = validate(input);
        if (JSON.stringify(errors) === JSON.stringify({})) {
            setLoadingSave(true);
            if (eventId) {
                let cmd: UpdateNonWorkingDayCommand = {
                    id: eventId,
                    idCountry: input?.idCountry,
                    date: input?.date,
                    description: input?.description
                };
                const response = await api.timeoffApi.apiVversionNonWorkingDayPut("1", cmd, {}).then((response) => {
                    if (response.data) {
                        toast.success({ body: intl.get('calendarEvent.toast.success.handleSave')});
                        setInput(defaultCalendarEventInput);
                        closeModal();
                        updateEvents();
                    };
                }).catch((error) => {
                    handleAPIError(error, toast, errors);
                    setErrors({ ...errors });
                }).finally(() => {
                    setLoadingSave(false);
                });
            } else {
                let cmd: CreateNonWorkingDayCommand = {
                    idCountry: input?.idCountry,
                    date: input?.date,
                    description: input?.description
                }
                const response = await api.timeoffApi.apiVversionNonWorkingDayPost("1", cmd, {}).then((response) => {
                    if (response.data) {
                        toast.success({ body: intl.get('calendarEvent.toast.success.handleSave.add')});
                        setInput(defaultCalendarEventInput);
                        closeModal();
                        updateEvents();
                    }
                }).catch((error) => {
                    handleAPIError(error, toast, errors);
                    setErrors({ ...errors });
                }).finally(() => {
                    setLoadingSave(false);
                });
            }
        }
    };

    const { openDialog, closeDialog } = useDialog();

    const handleDelete = (id: number) => {
        const dialogId = openDialog({
          title: intl.get('delete.modal.title'),
          message: intl.get('delete.modal.calendarEvent.message'),
          type: 'confirm',
          confirmButton: {
            confirmLabel: intl.get('delete.modal.delete.button'),
            handleConfirm: async () => {
              setLoading(true);
              try {
                const response = await api.timeoffApi.apiVversionNonWorkingDayIdDelete(eventId, "1", {});
                toast.success({ message: intl.get('calendarEvent.toast.success.handleDelete') });
                setLoading(false);
                updateEvents();
              } catch (error) {
                handleAPIError(error, toast, errors);
                setErrors({ ...errors });
              } finally {
                setLoading(false);
                closeDialog(dialogId);
              }
            },
          },
          cancelButton: {
            cancelLabel: intl.get('delete.modal.cancel.button'),
            handleCancel: () => {
              closeDialog(dialogId);
            },
          },
        });
      };

    return (
        <Card sx={{ m: 2 }}>
            <Box sx={{ p: 3 }}>
                <Typography color="primary" variant="h2" component="h2" sx={{ mb: 2 }}>
                    {eventId ? intl.get('calendarEvent.title.edit') : intl.get('calendarEvent.title.add')}
                </Typography>
                <Box sx={{ textAlign: 'start', mb: 3 }}>
                    {
                        loading === true ?
                        <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                            <Spinner small={true} />
                        </Box> :
                        <CalendarEventForm 
                            errors={errors}
                            input={input}
                            handleCountryChange={handleCountryChange}
                            countries={countries}
                            selectedCountry={input.country.name}
                            handleInputChange={handleInputChange}
                            handleDate={handleDate}
                        />
                    }
                </Box>
                <Box>
                    <Grid spacing={2}>
                    <Grid size={{xs:12, md:4}}>
                        <RequiredField/>
                    </Grid>
                    <Grid size={{xs:12, md:8}}>
                        <Stack direction="row" spacing={2} justifyContent="flex-end">
                            {
                                loadingSave === true ?
                                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                                    <Spinner small={true} />
                                </Box>
                                 :
                                <>
                                    <Button 
                                        variant="outlined"
                                        onClick={handleCancel}
                                    >
                                        <ArrowBackIcon/>
                                    </Button>
                                    {
                                        eventId &&
                                        <Button 
                                            variant="outlined"
                                            color="error"
                                            onClick={() => handleDelete(eventId)}
                                            startIcon={<DeleteOutlineIcon />}
                                        >
                                            {intl.get('delete.button')}
                                        </Button>
                                    }
                                    <Button 
                                        variant="contained"
                                        color="primary"
                                        onClick={handleSave}
                                        startIcon={<SaveOutlinedIcon />}
                                        disabled={JSON.stringify(errors) !== JSON.stringify({}) || input.country.name === "" ? true : false}
                                    >
                                        {intl.get('save.button')}
                                    </Button>
                                </>
                            }
                        </Stack>
                    </Grid>
                    </Grid>
                </Box>
            </Box>
        </Card>
    );
};

export default CalendarEvent;

